import { inject, Injectable } from '@angular/core';
import { Auth, signInWithCustomToken } from '@angular/fire/auth';
import { isNil } from 'es-toolkit';
import { BehaviorSubject, defer, EMPTY, filter, firstValueFrom, from, map, Observable, race, shareReplay, Subject, switchMap, throwError, throwIfEmpty } from 'rxjs';
import { take } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { Router } from '@angular/router';
import { firebaseConfig } from '@ss/typings/config';
import { PccTokens } from '@ss/typings/pcc';
import { Machine, MachineDefinition, State } from './machine';
import { Platform } from './platform';
import { TokenGroup } from './token';
import { Utils } from './utils';
import { AuthConfig } from './web-platform';
const MILLISECONDS_IN_FIVE_MINUTES = 300000;
const MINUTES_CHECK_EXP = MILLISECONDS_IN_FIVE_MINUTES;
const ROUTE_CHECK_STORAGE_KEY = 'ss-route-check-url';
const NTA_LAST_ACTIVE_KEY = 'ntala';
const PCC_CALLBACK_PATH = '/account/pcc-callback';
const HANDSHAKE_STATES = ['login', 'checkReply', 'postCode'];
const REFRESH_RETRY_DELAY_MS = 1000;
const MAX_REFRESH_ATTEMPTS = 2;

@Injectable({ providedIn: 'root' })
export class OAuthService {
	private config: AuthConfig;

	private platform: Platform;

	private params: any = {};

	private machine: Machine;

	private accessToken = new BehaviorSubject<{ accessToken: string; orgUuid: string; isValid: boolean }>(null);

	private accessTokenError = new Subject<unknown>();

	private stateChange = new BehaviorSubject<{ type: string; data: any }>(null);

	auth = inject(Auth);

	router = inject(Router);

	analytics = inject(Analytics);

	http = inject(HttpClient);


	/**
	 * Initialize the OAuth service with configuration
	 */
	public initialize(config: AuthConfig, platform: Platform): void {
		this.config = config;
		this.platform = platform;
		const params = new URL(window.location.href).searchParams;
		params.forEach((value, key) => this.params[key] = value);

		this.platform.get('state').then((stateStr) => {
			let state = State.fromString(stateStr);
			const {pathname} = window.location;
			const isPccCallback = pathname.endsWith(PCC_CALLBACK_PATH);
			const isSignInPage = pathname.endsWith('/account/signin') || pathname.endsWith('/account/signin-pcc');

			if (isSignInPage && HANDSHAKE_STATES.includes(state.type)) {
				this.platform.remove('state');
				state = new State('loggedOut', {});
			}

			this.machine = new Machine(this.platform, this.machineDefinition(), this.stateChange, state);

			const shouldResumeHandshake = isPccCallback && ['checkReply', 'postCode'].includes(state.type);
			const shouldResumeSession = !isPccCallback && (!['init', 'loggedOut'].includes(state.type) || config.quickStart);

			if (shouldResumeHandshake || shouldResumeSession) {
				this.machine.next();
				if (config.checkExpiring) {
					setInterval(() => {
						this.machine.transition(new State('checkExpired', null));
					}, MINUTES_CHECK_EXP);
				}
			}
		});
	}

	public getAccessToken(): Observable<{ accessToken: string; orgUuid: string; isValid: boolean }> {
		return this.accessToken.asObservable();
	}

	public getAccessTokenErrors(): Observable<unknown> {
		return this.accessTokenError.asObservable();
	}

	public getStateChanges(): Observable<State> {
		return this.stateChange.asObservable().pipe(filter((state) => state !== null));
	}

	public refreshTokenAndWait(): Observable<{ accessToken: string; orgUuid: string; isValid: boolean }> {
		return defer(() => {
			if (!this.machine) {
				return EMPTY;
			}
			if (['init', 'loggedOut'].includes(this.machine.getCurrentStateType())) {
				return EMPTY;
			}
			this.dispatch('checkExpired');
			return race(
				this.getAccessToken().pipe(
					filter((token: any) => !isNil(token?.accessToken) && token?.isValid),
					take(1),
				),
				this.getAccessTokenErrors().pipe(
					switchMap((error) => throwError(() => error)),
					take(1),
				),
			);
		});
	}

	public getPccOrgUuid(): Observable<string> {
		return this.stateChange.asObservable().pipe(filter((state) => state !== null), filter((state) => state.data.metadata.orgUuid), map((state) => state.data.metadata.orgUuid), shareReplay(1));
	}

	public dispatch(type: string): void {
		setTimeout(() => {
			if (!this.machine) return;
			if (type === 'login' && !HANDSHAKE_STATES.includes(this.machine.getCurrentStateType())) {
				this.machine.transition(new State('loggedOut', {}));
			}
			this.machine.transition(new State(type, null));
		});
	}

	private machineDefinition(): MachineDefinition {
		return {
			init: {
				function: () => {
					this.machine.transition(new State('login'));
				},
				validTransitions: ['login'],
			},
			login: {
				function: () => {
					this.handleLogin();
				},
				validTransitions: ['checkReply'],
			},
			checkReply: {
				function: (data) => {
					this.handleCheckReply(data);
				},
				validTransitions: ['postCode', 'loggedOut'],
			},
			postCode: {
				function: (data) => {
					this.handlePostCode(data);
				},
				validTransitions: ['checkExpired', 'loggedOut'],
			},
			checkExpired: {
				function: (data) => {
					this.handleCheckExpired(data);
				},
				validTransitions: ['refreshToken', 'logout', 'checkExpired', 'loggedOut'],
			},
			refreshToken: {
				function: (data) => {
					this.handleRefreshToken(data);
				},
				validTransitions: ['checkExpired', 'refreshToken', 'logout', 'loggedOut'],
			},
			logout: {
				function: (data) => {
					this.handleLogout(data);
				},
				validTransitions: ['loggedOut'],
			},
			loggedOut: {
				function: () => undefined,
				validTransitions: ['login'],
			},
		};
	}

	private handleLogin(): void {
		const STATE_LENGTH = 32;
		const state = Utils.base64URLEncode(Utils.random(STATE_LENGTH));
		const preservedRoute = window.localStorage.getItem(ROUTE_CHECK_STORAGE_KEY);
		const url =
			`https://connect.pointclickcare.com/auth/login?` +
			`redirect_uri=${this.config.redirectUri || window.location.origin}&` +
			`client_id=${this.config.clientId}&` +
			`state=${state}&` +
			`response_type=code&` +
			`scope=openid`;
		this.machine.transition(new State('checkReply', { state, url: preservedRoute || window.location.href }), true);
		this.platform.open(url).subscribe((params) => {
			this.params = params;
			this.machine.next();
		});
	}

	private handleCheckReply(data: { state: string; url: string }): void {
		if (this.params.error && this.params.error_description) {
			this.notifyAccessTokenError(this.params.error_description);
			this.failHandshake();
		} else if (this.params.code && data.state === this.params.state) {
			this.machine.transition(new State('postCode', { code: this.params.code, url: data.url }));
		} else {
			this.failHandshake();
		}
	}

	private handlePostCode(data: { code: string; url: string }): void {
		const body = new URLSearchParams({
			code: data.code,
			redirectUri: this.config.redirectUri || window.location.origin,
		}).toString();

		from(fetch(`${firebaseConfig.functionsUrl}/ntaApi/pcc/token`, {
			method: 'post',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body
		}))
			.pipe(
				filter((response) => response.ok),
				throwIfEmpty(),
				switchMap((response) => response.json()),
				switchMap(async(newTokens: PccTokens) => {
					if (newTokens.error) {
						return throwError(() => newTokens.error);
					}
					const tokenGroup = new TokenGroup(newTokens);
					if (tokenGroup.accessToken && tokenGroup.isAccessTokenValid()) this.accessToken.next({ accessToken: tokenGroup.accessToken, orgUuid: tokenGroup.orgUuid, isValid: true });
					if (newTokens.foundPccLink) {
						window.localStorage.setItem(NTA_LAST_ACTIVE_KEY, Date.now().toString());
						await signInWithCustomToken(this.auth, newTokens.googleToken);
						logEvent(this.analytics, 'login', { method: 'password' });
						if (this.config.preserveRoute && data.url) {
							this.platform.reopen(data.url).subscribe();
						} else {
							await this.router.navigate(['/']);
						}
					} else {
						await this.router.navigate(['/account/link-pcc']);
					}
					this.machine.transition(new State('checkExpired', { tokens: tokenGroup }), true);

					return tokenGroup.accessToken;
				}),
				// switchMap(() => this.config.preserveRoute ? this.platform.reopen(data.url) : EMPTY),
			)
			.subscribe({
				next: () => this.machine.next(),
				error: (error) => {
					this.notifyAccessTokenError(error);
					this.failHandshake();
				},
			});
	}

	private failHandshake(): void {
		this.machine.transition(new State('loggedOut', {}));
		this.platform.remove('state');
		this.router.navigate(['/account/signin']);
	}

	private handleCheckExpired(data: { tokens: any; refreshAttempts?: number }): void {
		if (data.tokens) {
			const tokens = new TokenGroup(data.tokens);
			if (tokens.accessToken && tokens.isAccessTokenValid()) {
				data.refreshAttempts = 0;
				this.accessToken.next({ accessToken: tokens.accessToken, orgUuid: tokens.orgUuid, isValid: true });
			} else if (tokens.refreshToken && tokens.isRefreshTokenValid()) {
				this.machine.transition(new State('refreshToken', data));
			} else {
				this.machine.transition(new State('logout', null));
			}
		}
	}

	private handleRefreshToken(data: { tokens: any; refreshAttempts?: number }): void {
		if (!data.tokens) return;
		const tokens = new TokenGroup(data.tokens);
		if (!tokens.refreshToken) {
			this.failRefresh(data.tokens);
			return;
		}
		const attempts = data.refreshAttempts ?? 0;
		fetch(`${firebaseConfig.functionsUrl}/ntaApi/pcc/refresh`, {
			method: 'post',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: `refreshToken=${tokens.refreshToken}`
		}).then(async (response) => {
			const newTokens: PccTokens = await response.json().catch(() => null);
			if (!response.ok || !newTokens || newTokens.error) {
				this.retryRefreshOrLogout(data.tokens, attempts, newTokens?.error ?? response.status);
				return;
			}
			const mergedTokens = new TokenGroup({ ...data.tokens, ...newTokens });
			this.machine.transition(new State('checkExpired', { tokens: mergedTokens, refreshAttempts: 0 }));
		}).catch((error) => this.retryRefreshOrLogout(data.tokens, attempts, error));
	}

	private retryRefreshOrLogout(tokens: any, attempts: number, error: unknown): void {
		const nextAttempt = attempts + 1;
		if (nextAttempt >= MAX_REFRESH_ATTEMPTS) {
			this.notifyAccessTokenError(error);
			this.failRefresh(tokens);
			return;
		}
		setTimeout(() => {
			if (this.machine.getCurrentStateType() !== 'refreshToken') return;
			this.machine.transition(new State('refreshToken', { tokens, refreshAttempts: nextAttempt }));
		}, REFRESH_RETRY_DELAY_MS);
	}

	private failRefresh(tokens: any): void {
		this.machine.transition(new State('logout', { tokens }));
	}

	private notifyAccessTokenError(error: unknown): void {
		this.accessToken.next(null);
		this.accessTokenError.next(error);
	}

	private async handleLogout(data: { tokens: any }): Promise<void> {
		try {
			if (data.tokens) {
				const tokens = new TokenGroup(data.tokens);
				await firstValueFrom(this.http.post(`${firebaseConfig.functionsUrl}/ntaApi/pcc/revoke?skipPccAuth=true&skipAuth=true`, {
					refreshToken: tokens.refreshToken,
					accessToken: tokens.accessToken
				}));
			}
		} catch {
			return;
		} finally {
			this.machine.transition(new State('loggedOut', {}));
			this.accessToken.next(null);
			this.platform.remove('state');
		}
	}
}
